"use client";

import { useEffect } from "react";
import { SIZE_SELECTOR_ERROR_NO_SIZE_SELECTED } from "@/constants/constants.ts";
import { useShowStoreStockButton } from "@/hooks/useShowStoreStockButton/useShowStoreStockButton.tsx";

declare global {
  interface Window {
    isSizeSelected: () => boolean;
  }
}

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace React.JSX {
    export interface IntrinsicElements {
      "jl-store-stock": {
        "data-testid"?: string;
        productcode?: string;
        productname?: string;
        skuid?: string;
        buttontext?: string;
        isprimarybutton?: string;
        isdisabled?: string;
        isloading?: string;
        injectinline?: string;
      };
    }
  }
}

export type Props = {
  displayCode: string;
  isFutureRelease: boolean;
  isPreOrderable: boolean;
  skuId: string;
  stockApiBaseUrl: string;
  stockApiKey: string;
  storeStockApiKey: string;
  storeStockModalApiUrl: string;
  storeStockScriptUrl: string;
  title: string;
  usePrimaryButton: boolean;
  isVariantSelected?: boolean;
};

export const StoreStock = ({
  title,
  displayCode,
  skuId,
  isFutureRelease,
  isPreOrderable,
  usePrimaryButton,
  storeStockScriptUrl,
  stockApiBaseUrl,
  stockApiKey,
  storeStockApiKey,
  storeStockModalApiUrl,
  isVariantSelected = false,
}: Props) => {
  const showStoreStockButton = useShowStoreStockButton({
    storeStockApiKey,
    storeStockModalApiUrl,
    stockApiBaseUrl,
    stockApiKey,
    skuId,
    displayCode,
    isFutureRelease,
    isPreOrderable,
  });

  const buttonText =
    showStoreStockButton === "isLoading"
      ? "Loading..."
      : showStoreStockButton === "disabled"
        ? "Not available in store"
        : "Check in-store stock";

  useEffect(() => {
    window.isSizeSelected = () => {
      if (!isVariantSelected) {
        window.dispatchEvent(
          new CustomEvent(SIZE_SELECTOR_ERROR_NO_SIZE_SELECTED),
        );
        return false;
      } else {
        return true;
      }
    };
  }, [isVariantSelected]);

  useEffect(() => {
    if (!storeStockScriptUrl) return;

    const existingScript = document.querySelector(
      `script[src="${storeStockScriptUrl}"]`,
    );
    if (existingScript) return;

    const jlStoreStockScript = document.createElement("script");
    jlStoreStockScript.setAttribute("type", "text/javascript");
    jlStoreStockScript.setAttribute("src", storeStockScriptUrl);

    document.body.appendChild(jlStoreStockScript);

    return () => {
      if (document.body.contains(jlStoreStockScript)) {
        document.body.removeChild(jlStoreStockScript);
      }
    };
  }, [storeStockScriptUrl]);

  return (
    <div
      id="product:tools:store-stock"
      data-testid="product:tools:store-stock"
      aria-busy={showStoreStockButton === "isLoading"}
    >
      <jl-store-stock
        buttontext={buttonText}
        data-testid="store-stock"
        isdisabled={showStoreStockButton === "disabled" ? "true" : "false"}
        isloading={showStoreStockButton === "isLoading" ? "true" : "false"}
        isprimarybutton={usePrimaryButton ? "true" : "false"}
        productcode={displayCode}
        productname={title}
        skuid={skuId}
        injectinline="true"
      />
    </div>
  );
};
